import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input } from '@/components/ui/input.jsx';
import { Textarea } from '@/components/ui/textarea.jsx';
import Select from '@/components/ui/select.jsx';
import { Button } from '@/components/ui/button.jsx';

const schema = yup.object({
  nombre: yup.string().required('El nombre es obligatorio'),
  apellido: yup.string().required('El apellido es obligatorio'),
  empresaId: yup.string().required('Selecciona una empresa'),
  email: yup.string().email('Formato de email inválido').nullable().transform(value => value || null),
  dni: yup
    .string()
    .nullable()
    .transform(value => value || null)
    .matches(/^\d{7,8}$/u, 'El DNI debe tener 7 u 8 dígitos')
    .optional(),
  cuil: yup
    .string()
    .nullable()
    .transform(value => value || null)
    .matches(/^(20|23|24|27|30|33|34)-?\d{7,8}-?\d$/u, 'Formato de CUIL inválido')
    .optional(),
  salario: yup
    .number()
    .typeError('El salario debe ser un número')
    .nullable()
    .transform(value => (value === '' || Number.isNaN(value) ? null : value))
    .min(0, 'El salario no puede ser negativo'),
});

const estadoOptions = [
  { value: 'ACTIVO', label: 'Activo' },
  { value: 'INACTIVO', label: 'Inactivo' },
  { value: 'BAJA', label: 'Baja' },
];

// Formulario reutilizable para altas y ediciones de empleados
export function EmpleadoForm({ defaultValues, onSubmit, submitting }) {
  const formId = useId();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <form className="space-y-8" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label htmlFor={`${formId}-nombre`} className="block text-sm font-semibold text-[var(--text-primary)]">Nombre *</label>
          <Input id={`${formId}-nombre`} placeholder="Ej. Julieta" {...register('nombre')} />
          {errors.nombre && <p className="mt-1 text-xs text-rose-500">{errors.nombre.message}</p>}
        </div>
        <div>
          <label htmlFor={`${formId}-apellido`} className="block text-sm font-semibold text-[var(--text-primary)]">Apellido *</label>
          <Input id={`${formId}-apellido`} placeholder="Ej. Vera" {...register('apellido')} />
          {errors.apellido && <p className="mt-1 text-xs text-rose-500">{errors.apellido.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Legajo</label>
          <Input placeholder="Ej. A108" {...register('legajo')} />
        </div>
        <div>
          <label htmlFor={`${formId}-empresa`} className="block text-sm font-semibold text-[var(--text-primary)]">Empresa *</label>
          <Input id={`${formId}-empresa`} placeholder="Ej. emp-001" {...register('empresaId')} />
          {errors.empresaId && <p className="mt-1 text-xs text-rose-500">{errors.empresaId.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Sucursal</label>
          <Input placeholder="Ej. caba" {...register('sucursalId')} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Puesto</label>
          <Input placeholder="Ej. People Partner" {...register('puesto')} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Categoría</label>
          <Input placeholder="Ej. Sr" {...register('categoria')} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Estado</label>
          <Select {...register('estado')}>
            {estadoOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Email</label>
          <Input type="email" placeholder="Ej. julieta.vera@sinamonsoft.com" {...register('email')} />
          {errors.email && <p className="mt-1 text-xs text-rose-500">{errors.email.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Teléfono</label>
          <Input placeholder="Ej. +54 11 4300-1100" {...register('telefono')} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">DNI</label>
          <Input placeholder="Ej. 29900123" {...register('dni')} />
          {errors.dni && <p className="mt-1 text-xs text-rose-500">{errors.dni.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">CUIL</label>
          <Input placeholder="Ej. 27-29900123-1" {...register('cuil')} />
          {errors.cuil && <p className="mt-1 text-xs text-rose-500">{errors.cuil.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Fecha de ingreso</label>
          <Input type="date" {...register('fechaIngreso')} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Fecha de nacimiento</label>
          <Input type="date" {...register('fechaNacimiento')} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Salario</label>
          <Input type="number" step="1000" placeholder="Ej. 280000" {...register('salario')} />
          {errors.salario && <p className="mt-1 text-xs text-rose-500">{errors.salario.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-semibold text-[var(--text-primary)]">Avatar (URL)</label>
          <Input placeholder="https://..." {...register('avatarUrl')} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-semibold text-[var(--text-primary)]">Dirección</label>
        <Input placeholder="Ej. Amenábar 999" {...register('direccion')} />
      </div>
      <div>
        <label className="block text-sm font-semibold text-[var(--text-primary)]">Notas internas</label>
        <Textarea rows={4} placeholder="Observaciones, historial, etc." {...register('notas')} />
      </div>
      <div className="flex items-center justify-end gap-3">
        <Button type="submit" className="rounded-2xl bg-brand-teal px-8 text-white hover:bg-emerald-500" disabled={submitting}>
          {submitting ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </div>
    </form>
  );
}

export default EmpleadoForm;
